import { parseDate } from '@northfork/basics'
import { isAfter } from 'date-fns'
import { ExternalUnmatchedTransaction } from '../model/single-transaction/external-unmatched-transaction'
import {
  WireTransfer,
  WireTransferModel,
} from '../model/single-transaction/wire-transfer/wire-transfer.model'

/**
 * Function for Create FxSpot mapping
 */
export function generateCashWireCreateMapping(): WireTransferModel[] {
  return [
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: 'CCYUSD',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: '',
      errorMessage: '',
    },

    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: '',
      errorMessage: '',
    },
    {
      id: 'sourceAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Source Account',
      value: '',
      errorMessage: '',
    },

    {
      id: 'sourcePostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Source Post Date',
      value: '',
      errorMessage: '',
    },
    {
      id: 'sourceCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Source Cleared',
      value: true,
      errorMessage: '',
    },
    {
      id: 'destAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Dest Account',
      value: '',
      errorMessage: '',
    },
    {
      id: 'destPostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Destination Post Date',
      value: '',
      errorMessage: '',
    },
    {
      id: 'destCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Destination Cleared',
      value: true,
      errorMessage: '',
    },
    {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Amount',
      value: '',
      errorMessage: '',
    },
  ]
}

/**
 * Function for Edit CashWire mapping
 */
export function generateCashWireEditMapping(
  wireTransaction: WireTransfer,
  cusip: string,
): WireTransferModel[] {
  return [
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset ID',
      value: cusip,
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: wireTransaction.cwanIdentifier ?? '',
      errorMessage: '',
    },

    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: wireTransaction.entryDate,
      errorMessage: '',
    },
    {
      id: 'sourceAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Source Account',
      value: wireTransaction.accountId.toString(),
      errorMessage: '',
    },

    {
      id: 'sourcePostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Source Post Date',
      value: wireTransaction.destPostDate ?? '',
      errorMessage: '',
    },
    {
      id: 'sourceCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Source Cleared',
      value: wireTransaction.sourceClearedDate ? true : false,
      errorMessage: '',
    },
    {
      id: 'destAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Dest Account',
      value: wireTransaction.destAccountId?.toString() ?? '',
      errorMessage: 'Dest Account is required',
    },
    {
      id: 'destPostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Destination Post Date',
      value: wireTransaction.destPostDate ?? '',
      errorMessage: '',
    },
    {
      id: 'destCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Destination Cleared',
      value: wireTransaction.destClearedDate ? true : false,
      errorMessage: '',
    },
    {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: wireTransaction.units.toString(),
      errorMessage: '',
    },
  ]
}

/**
 * Function for External CashWire mapping
 */
export function generateCashWireExternalMapping(
  wireTransaction: ExternalUnmatchedTransaction,
  accountId: string,
  isAggregate: boolean,
): WireTransferModel[] {
  return [
    {
      id: 'cusip',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Asset ID',
      value: '',
      errorMessage: '',
    },
    {
      id: 'cwanIdentifier',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Asset Identifier',
      value: wireTransaction.cwanIdentifier ?? '',
      errorMessage: '',
    },

    {
      id: 'entryDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Entry Date',
      value: wireTransaction.entryDate,
      errorMessage: '',
    },
    {
      id: 'sourceAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: false,
      field: 'Source Account',
      value: accountId,
      errorMessage: '',
    },

    {
      id: 'sourcePostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Source Post Date',
      value: wireTransaction.postDate ?? '',
      errorMessage: '',
    },
    {
      id: 'sourceCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Source Cleared',
      value: isDateCleared(wireTransaction.postDate) ? true : false,
      errorMessage: '',
    },
    {
      id: 'destAccountId',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Dest Account',
      value: '',
      errorMessage: 'Dest Account is required',
    },
    {
      id: 'destPostDate',
      validityStatus: 'valid',
      type: 'date',
      isEditable: true,
      field: 'Destination Post Date',
      value: wireTransaction.postDate ?? '',
      errorMessage: '',
    },
    {
      id: 'destCleared',
      validityStatus: 'valid',
      type: 'string',
      isEditable: true,
      field: 'Destination Cleared',
      value: isDateCleared(wireTransaction.postDate) ? true : false,
      errorMessage: '',
    },
    {
      id: 'units',
      validityStatus: 'valid',
      type: 'number',
      isEditable: true,
      field: 'Units',
      value: wireTransaction.amount
        ? Math.abs(wireTransaction.amount).toString()
        : wireTransaction.units
        ? Math.abs(wireTransaction.units).toString()
        : '',
      errorMessage: 'Units is required',
    },
  ]
}

/**
 * Function for External CashWire mapping
 */
function isDateCleared(postDate: string) {
  if (postDate != null && isAfter(new Date(), parseDate(postDate))) {
    return true
  } else {
    return false
  }
}
